import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { api, errText, evalSessionKey } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PlenaLogo } from "@/components/brand/PlenaLogo";
import { Spinner } from "@/components/common";
import LandingPage from "./LandingPage";
import CategoryPriorityPage from "./CategoryPriorityPage";
import WorkspacePage from "./WorkspacePage";
import CandidatePage from "./CandidatePage";
import RankingPage from "./RankingPage";
import ReviewPage from "./ReviewPage";
import DonePage from "./DonePage";

const Ctx = createContext(null);
export const useEvaluation = () => useContext(Ctx);

const isAnswered = (e) => !!e && (e.score !== null && e.score !== undefined || e.evidence_status === "INSUFFICIENT");

function recompute(ctx) {
  const total = ctx.criteria.length + 1;
  const candidates = ctx.candidates.map((c) => {
    const ev = ctx.evaluations[c.id] || {};
    const crit = ev.criteria || {};
    const done = ctx.criteria.filter((cr) => isAnswered(crit[cr.id])).length + (ev.overall_score != null ? 1 : 0);
    return { ...c, progress: { done, total, status: total && done === total ? "COMPLETED" : done > 0 ? "IN_PROGRESS" : "PENDING", overall_done: ev.overall_score != null } };
  });
  const ranking = ctx.category_ranking?.length ? ctx.category_ranking : ctx.criteria.map((cr, i) => ({ criterion_id: cr.id, position: i + 1, tier: null }));
  const category_ranking_complete = ranking.length === ctx.criteria.length && ranking.every((r) => r.tier);
  return { ...ctx, candidates, category_ranking: ranking, category_ranking_complete, progress: { evaluated: candidates.filter((c) => c.progress.status === "COMPLETED").length, total: candidates.length } };
}

const ERRORS = {
  NOT_FOUND: ["Bağlantı geçerli değil", "Bu değerlendirme bağlantısı bulunamadı. Lütfen size gönderilen bağlantıyı kontrol edin."],
  REVOKED: ["Bağlantı devre dışı", "Bu değerlendirme bağlantısı devre dışı bırakıldı. Sorularınız için sizi davet eden kişiyle iletişime geçin."],
  EXPIRED: ["Bağlantının süresi doldu", "Bu bağlantı artık geçerli değil. Yeni bir bağlantı için sizi davet eden kişiyle iletişime geçin."],
  NETWORK: ["Bağlantı kurulamadı", "İnternet bağlantınızı kontrol edip sayfayı yenileyin."],
};

export function ErrorScreen({ code }) {
  const [title, text] = ERRORS[code] || ERRORS.NOT_FOUND;
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="evaluator-error">
      <PlenaLogo height={26} />
      <h1 className="mt-10 font-heading text-2xl font-bold">{title}</h1>
      <p className="mt-2 max-w-md text-sm text-plena-text2">{text}</p>
    </div>
  );
}

export function AccessCodeScreen({ token, onUnlocked }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post(`/e/${token}/unlock`, { code });
      if (r.data.session) sessionStorage.setItem(evalSessionKey(token), r.data.session);
      onUnlocked();
    } catch (ex) { setErr(errText(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center px-6" data-testid="access-code-screen">
      <form onSubmit={submit} className="fade-up w-full max-w-sm">
        <PlenaLogo height={26} />
        <p className="eyebrow mt-10">Aday Değerlendirmesi</p>
        <h1 className="mt-2 font-heading text-2xl font-bold">Erişim şifresi</h1>
        <p className="mt-2 text-sm text-plena-text2">Bu değerlendirme bağlantısı şifre korumalıdır. Size iletilen erişim şifresini girin.</p>
        <input data-testid="access-code-input" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" required minLength={4} className="mt-6 h-12 w-full rounded-[10px] border border-plena-border bg-white px-4 text-center font-heading text-xl font-semibold tracking-[0.3em] outline-none focus:border-plena-blue" placeholder="••••••" />
        {err && <p data-testid="access-code-error" role="alert" className="mt-3 text-sm text-plena-coral">{err}</p>}
        <Button data-testid="access-code-submit" type="submit" disabled={busy || !code} className="btn-press mt-4 h-11 w-full rounded-[10px] font-semibold">{busy ? "Kontrol ediliyor…" : "Devam"}</Button>
      </form>
    </div>
  );
}

export default function EvaluatorApp() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const timer = useRef(null);
  const mutations = useRef(0);

  const reload = useCallback(async () => {
    const seq = mutations.current;
    try { const r = await api.get(`/e/${token}`); if (seq !== mutations.current) return; setCtx(recompute(r.data)); setError(null); }
    catch (e) {
      const code = e.response ? (e.response.data?.detail?.code || "NOT_FOUND") : "NETWORK";
      if (code === "CODE_REQUIRED") sessionStorage.removeItem(evalSessionKey(token));
      setError(code);
    }
  }, [token]);
  useEffect(() => { reload(); }, [reload]);

  const track = useCallback(async (fn) => {
    mutations.current += 1;
    setSaveState("saving");
    clearTimeout(timer.current);
    try { const r = await fn(); setSaveState("saved"); timer.current = setTimeout(() => setSaveState("idle"), 2000); return r; }
    catch (e) { setSaveState("error"); throw new Error(errText(e, "Kaydedilemedi. Bağlantınızı kontrol edin.")); }
  }, []);

  const saveCriterion = useCallback((cid, crid, payload) => {
    setCtx((c) => recompute({ ...c, evaluations: { ...c.evaluations, [cid]: { ...c.evaluations[cid], criteria: { ...c.evaluations[cid]?.criteria, [crid]: payload } } } }));
    return track(() => api.put(`/e/${token}/candidates/${cid}/criteria/${crid}`, payload));
  }, [token, track]);

  const saveComment = useCallback((cid, general_comment) => {
    setCtx((c) => ({ ...c, evaluations: { ...c.evaluations, [cid]: { ...c.evaluations[cid], general_comment } } }));
    return track(() => api.put(`/e/${token}/candidates/${cid}`, { general_comment }));
  }, [token, track]);

  const saveRanking = useCallback((order) => {
    setCtx((c) => ({ ...c, ranking_touched: true, candidates: order.map((id) => c.candidates.find((x) => x.id === id)) }));
    return track(() => api.put(`/e/${token}/ranking`, { order }));
  }, [token, track]);

  const saveCategoryRanking = useCallback((items) => {
    setCtx((c) => recompute({ ...c, category_ranking: items.map((it, i) => ({ ...it, position: i + 1 })) }));
    return track(() => api.put(`/e/${token}/category-ranking`, { items: items.map((it) => ({ criterion_id: it.criterion_id, tier: it.tier || null })) }));
  }, [token, track]);

  const saveOverall = useCallback((cid, overall_score) => {
    setCtx((c) => recompute({ ...c, evaluations: { ...c.evaluations, [cid]: { ...c.evaluations[cid], overall_score } } }));
    return track(() => api.put(`/e/${token}/candidates/${cid}/overall`, { overall_score }));
  }, [token, track]);

  const submit = useCallback(async () => {
    const r = await api.post(`/e/${token}/submit`);
    setCtx((c) => ({ ...c, status: "SUBMITTED", submitted_at: r.data.submitted_at }));
  }, [token]);

  const value = useMemo(() => ({ ctx, token, saveState, saveCriterion, saveComment, saveRanking, saveCategoryRanking, saveOverall, submit, reload }), [ctx, token, saveState, saveCriterion, saveComment, saveRanking, saveCategoryRanking, saveOverall, submit, reload]);

  if (error === "CODE_REQUIRED") return <AccessCodeScreen token={token} onUnlocked={reload} />;
  if (error) return <ErrorScreen code={error} />;
  if (!ctx) return <div className="flex min-h-screen items-center justify-center"><Spinner /></div>;
  const base = `/e/${token}`;
  const locked = ctx.status === "SUBMITTED";

  return (
    <Ctx.Provider value={value}>
      <Routes>
        {locked ? (
          <>
            <Route path="done" element={<DonePage />} />
            <Route path="*" element={<Navigate to={`${base}/done`} replace />} />
          </>
        ) : (
          <>
            <Route index element={<LandingPage />} />
            <Route path="priorities" element={<CategoryPriorityPage />} />
            <Route path="candidates" element={<WorkspacePage />} />
            <Route path="candidates/:cid" element={<CandidatePage />} />
            <Route path="ranking" element={<RankingPage />} />
            <Route path="review" element={<ReviewPage />} />
            <Route path="*" element={<Navigate to={base} replace />} />
          </>
        )}
      </Routes>
    </Ctx.Provider>
  );
}
